import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import { ILookat, ILookatConfig } from "../../../models/interactions/Lookat";
import PiActorChipSelect from "../components/PiActorChipSelect";
import PiActorPropChipSelect from "../components/PiActorPropChipSelect";
import PiAddLink from "../components/PiAddLink";
import PiConnectorLabel from "../components/PiConnectorLabel";
import PiLookModeChipSelect from "../components/PiLookModeChipSelect";
import "./LookatPreviewBody.css";
import { hasLookatTarget } from "../../../models/NoLookat";

/**
 * One sentence per look: "<actor> looks at → <actor or prop>", then the Look chip
 * (whole body, or head only via FinalIK).
 * Design: Design Studio `screens/lookat-rework/r1-lookat.html`.
 */
export default function LookatPreviewBody() {
  const interaction = useSelector(selectSelectedElement) as ILookat;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const save = (lookats: ILookatConfig[]) =>
    dispatch(updateSelectedInteraction({ ...interaction, lookats } as ILookat));

  const addLookatEntry = () =>
    save([...interaction.lookats, { sourceNpcId: "", targetId: "" }]);

  const deleteEntry = (index: number) =>
    save(interaction.lookats.filter((_, i) => i !== index));

  const updateLookat = (index: number, lookat: ILookatConfig) => {
    const lookats = [...interaction.lookats];
    lookats[index] = lookat;
    save(lookats);
  };

  const rows = interaction.lookats.map((lookat, index) => {
    // An actor can only drive one look per interaction.
    const usedByOtherRows = interaction.lookats
      .filter((_, i) => i !== index)
      .map((other) => other.sourceNpcId);

    return (
      <div
        key={index}
        className="lookat-row flex items-center gap-3 min-h-[60px] px-3 rounded-lg bg-interaction-primary-washed"
        data-testid="lookat-row"
      >
        <span className="lookat-row__actor flex">
          <PiActorChipSelect
            aria-label={String(t("lookat_ui.actor"))}
            value={lookat.sourceNpcId}
            excludeActors={usedByOtherRows}
            onChange={(id) =>
              updateLookat(index, {
                ...lookat,
                sourceNpcId: id,
                // an actor cannot look at itself
                targetId: lookat.targetId === id ? "" : lookat.targetId,
              })
            }
          />
        </span>
        <span className="lookat-row__connector flex">
          <PiConnectorLabel text={String(t("lookat_ui.looks_at"))} />
        </span>
        <span className="lookat-row__target flex">
          <PiActorPropChipSelect
            aria-label={String(t("lookat_ui.target"))}
            value={lookat.targetId}
            excludeActors={lookat.sourceNpcId ? [lookat.sourceNpcId] : []}
            onChange={(id) => updateLookat(index, { ...lookat, targetId: id })}
            allowNoActor
          />
        </span>
        {/* the look mode only means something once there is a target */}
        <span className="lookat-row__look flex">
          <PiLookModeChipSelect
            value={lookat.finalIK}
            onChange={(finalIK) => updateLookat(index, { ...lookat, finalIK })}
            disabled={!hasLookatTarget(lookat.targetId)}
          />
        </span>
        {interaction.lookats.length > 1 && (
          <button
            type="button"
            aria-label={String(t("delete"))}
            data-testid="lookat-delete"
            onClick={() => deleteEntry(index)}
            className="lookat-row__delete ml-auto flex p-1 rounded-[6px] bg-transparent border-none cursor-pointer text-interaction-primary-faded transition-colors hover:text-creator-error focus-visible:text-creator-error outline-none"
          >
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
          </button>
        )}
      </div>
    );
  });

  return (
    <div className="lookat-rows flex flex-col gap-2 min-h-0 overflow-y-auto pit-scrollbar">
      {rows}
      <div className="mt-1">
        <PiAddLink text={String(t("lookat_ui.add_look"))} onClick={addLookatEntry} />
      </div>
    </div>
  );
}
